"use client";

import {
  Activity,
  ArrowRight,
  BarChart3,
  CheckCircle2,
  Clock3,
  Route,
  Sparkles,
  Workflow,
} from "lucide-react";

import {
  AskWayak,
  KpiCard,
  PageHeader,
  ProductFrame,
  StatusPill,
} from "./ProductPreview";

export function ServicesProductPreview() {
  const workstreams = [
    [
      "01",
      "Map the bottleneck",
      "Claims intake · 4.6 day baseline",
      "Complete",
    ],
    [
      "02",
      "Connect the context",
      "Policy, claim, documents, ownership",
      "Complete",
    ],
    ["03", "Run the pilot", "42 records · human review enabled", "Running"],
    ["04", "Measure and scale", "Cycle, touch time, exception rate", "Next"],
  ];
  return (
    <ProductFrame active="monitor" className="pv-stage-frame sv-preview">
      <div className="pv-page sv-page">
        <PageHeader
          icon={Activity}
          title="Pilot impact"
          description="Illustrative Wayak engagement workspace · claims intake pilot"
        />
        <div className="sv-meta">
          <span>Sanitized demo data</span>
          <StatusPill tone="sky">Week 3 of 4</StatusPill>
        </div>
        <AskWayak placeholder="Ask Wayak where the pilot is creating leverage..." />
        <div className="sv-kpis">
          <KpiCard label="Baseline cycle" value="4.6d" icon={Clock3} />
          <KpiCard
            label="Pilot median"
            value="1.2d"
            icon={BarChart3}
            tone="green"
          />
          <KpiCard label="Human review" value="18%" icon={Route} tone="sky" />
        </div>
        <div className="sv-grid">
          <section className="sv-workstreams">
            <div className="sv-panel-head">
              <span>
                <Workflow size={12} /> Engagement plan
              </span>
              <span>4 workstreams</span>
            </div>
            {workstreams.map(([n, title, body, status], index) => (
              <div className="sv-workstream" key={n}>
                <span
                  className={
                    index < 2 ? "is-done" : index === 2 ? "is-running" : ""
                  }
                >
                  {index < 2 ? <CheckCircle2 size={12} /> : n}
                </span>
                <div>
                  <strong>{title}</strong>
                  <small>{body}</small>
                </div>
                <StatusPill
                  tone={index < 2 ? "green" : index === 2 ? "sky" : "slate"}
                >
                  {status}
                </StatusPill>
              </div>
            ))}
          </section>
          <section className="sv-impact">
            <div className="sv-panel-head">
              <span>
                <BarChart3 size={12} /> Cycle-time movement
              </span>
              <span>Illustrative</span>
            </div>
            <div className="sv-bars">
              <div>
                <span>Baseline</span>
                <i style={{ width: "92%" }} />
                <strong>4.6d</strong>
              </div>
              <div>
                <span>Pilot</span>
                <i style={{ width: "24%" }} />
                <strong>1.2d</strong>
              </div>
            </div>
            <div className="sv-insight">
              <span>
                <Sparkles size={13} />
              </span>
              <div>
                <small>Wayak finding</small>
                <p>
                  Most elapsed time sat between document intake and completeness
                  review—not in adjuster judgment.
                </p>
                <strong>
                  Next: expand to two intake channels <ArrowRight size={10} />
                </strong>
              </div>
            </div>
          </section>
        </div>
      </div>
    </ProductFrame>
  );
}

export function ServicesProductPreviewStyles() {
  return (
    <style>{`
      @keyframes svActivePilot { 0%,52%,100% { box-shadow: 0 0 0 0 rgba(3,105,161,0); transform: scale(1); } 64%,78% { box-shadow: 0 0 0 4px rgba(3,105,161,.09); transform: scale(1.04); } }
      @keyframes svBarSettle { from { opacity: .35; transform: scaleX(.22); } to { opacity: 1; transform: scaleX(1); } }
      @keyframes svInsightEnter { from { opacity: 0; transform: translateY(7px); } to { opacity: 1; transform: none; } }

      .sv-preview { margin-top: 0; min-height: 0; }
      .sv-page { padding-bottom: 18px !important; }
      .sv-meta { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-top: 13px; color: var(--pv-muted); font-size: 7px; letter-spacing: .09em; text-transform: uppercase; }
      .sv-kpis { display: grid; grid-template-columns: repeat(3,1fr); gap: 7px; margin-top: 10px; }
      .sv-grid { display: grid; grid-template-columns: 1.1fr .9fr; gap: 8px; margin-top: 9px; }
      .sv-workstreams, .sv-impact { min-width: 0; padding: 11px; border: 1px solid var(--pv-border); border-radius: 12px; background: #fff; }
      .sv-panel-head { display: flex; align-items: center; justify-content: space-between; gap: 8px; padding-bottom: 8px; border-bottom: 1px solid var(--pv-border); color: var(--pv-muted); font-size: 7px; }
      .sv-panel-head > span:first-child { display: flex; align-items: center; gap: 5px; color: var(--pv-ink); font-size: 8px; font-weight: 700; }
      .sv-workstream { display: grid; grid-template-columns: 25px minmax(0,1fr) auto; align-items: center; gap: 7px; padding: 8px 0; border-bottom: 1px solid #efeff1; }
      .sv-workstream:last-child { border-bottom: 0; padding-bottom: 0; }
      .sv-workstream > span:first-child { width: 25px; height: 25px; display: grid; place-items: center; border: 1px solid #d4d4d8; border-radius: 7px; color: #71717a; font-size: 7px; }
      .sv-workstream > span.is-done { border-color: #bbf7d0; background: #f0fdf4; color: #166534; }
      .sv-workstream > span.is-running { border-color: #bae6fd; background: #f0f9ff; color: #075985; animation: svActivePilot 3.8s ease-in-out infinite; }
      .sv-workstream strong, .sv-workstream small { display: block; }
      .sv-workstream strong { font-size: 8px; }
      .sv-workstream small { margin-top: 2px; color: var(--pv-muted); font-size: 7px; }
      .sv-bars { display: grid; gap: 9px; margin-top: 12px; }
      .sv-bars > div { display: grid; grid-template-columns: 45px 1fr 28px; align-items: center; gap: 6px; font-size: 7px; }
      .sv-bars i { height: 7px; display: block; border-radius: 99px; background: #18181b; transform-origin: left center; animation: svBarSettle .85s cubic-bezier(.16,1,.3,1) both; }
      .sv-bars > div:last-child i { background: #166534; }
      .sv-bars > div:last-child i { animation-delay: .14s; }
      .sv-bars strong { text-align: right; font-size: 7px; }
      .sv-insight { display: grid; grid-template-columns: 26px 1fr; gap: 7px; margin-top: 13px; padding: 10px; border-radius: 10px; background: var(--pv-soft); animation: svInsightEnter .5s cubic-bezier(.16,1,.3,1) .24s both; }
      .sv-insight > span { width: 26px; height: 26px; display: grid; place-items: center; border-radius: 8px; background: #18181b; color: #fff; }
      .sv-insight small { color: var(--pv-muted); font-size: 6px; letter-spacing: .08em; text-transform: uppercase; }
      .sv-insight p { margin: 5px 0 0; color: #3f3f46; font-size: 7px; line-height: 1.45; }
      .sv-insight strong { display: flex; align-items: center; gap: 4px; margin-top: 7px; font-size: 7px; }
      @media (max-width: 820px) { .sv-grid { grid-template-columns: 1fr; } }
      @media (max-width: 520px) { .sv-kpis { grid-template-columns: 1fr 1fr; } .sv-kpis > :last-child { grid-column: 1 / -1; } }
      @media (prefers-reduced-motion: reduce) { .sv-workstream > span.is-running,.sv-bars i,.sv-insight { animation: none !important; transform: none !important; } }
    `}</style>
  );
}
